import {
  fetchImageAsBase64,
  getMediaBasePath,
  getTeacherSubjectName,
} from '@geneo2-web/shared-ui';
import { Box } from '@mui/material';
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import {
  ContentElementType,
  ImageOption,
  ImageType,
  McqMultipleContentModel,
  McqSingleContentModel,
  OptionsType,
  Question,
  SolutionElementType,
} from '@protos/content_management/content.db_pb';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { PrintableAssessmentHtml } from './PrintableAssessmentHtml';
import { renderElementToPdf } from './pdfExportUtils';
import {
  useAppDispatch,
  useAppSelector,
} from 'apps/teacher/src/reduxStore/reduxHooks';
import { setToastInfo } from '../../../Home/reducer/homeDashboard.slice';
import { isGifMp4 } from 'libs/shared-ui/src/components/elements/ImageWrapper';
import { default as Geneologo } from 'libs/shared-ui/src/assets/images/geneo-logo.png';
import { getSubjectNameFromEnum } from '../../../Home/home.utils';

export interface PrintableAssessmentRef {
  handlePrint: (params: {
    title?: string;
    setName?: string;
    questions?: Question[];
    showAnswer?: boolean;
  }) => Promise<void>;
}

interface PrintableAssessmentProps {
  assessment?: AssessmentContent;
  schoolLogo?: string;
  printableFontSize?: number;
  setLoading?: (loading: boolean) => void;
}

const PrintableAssessment = forwardRef<
  PrintableAssessmentRef,
  PrintableAssessmentProps
>(({ assessment, schoolLogo, printableFontSize = 16, setLoading }, ref) => {
  const printableRef = useRef<HTMLDivElement>(null);
  const [showData, setShowData] = useState(false);
  const dispatch = useAppDispatch();
  const [printState, setPrintState] = useState<{
    questions?: Question[];
    showAnswer?: boolean;
    setName?: string;
  }>({});
  const { user_info } = useAppSelector((state) => state.auth);

  const questionsList = assessment?.assessmentContent ?? [];

  const sanitizeFilenamePart = (part: string) =>
    part
      .replace(/[^a-zA-Z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');

  const waitForImagesToLoad = async (container: HTMLElement) => {
    const imgs = Array.from(container.querySelectorAll('img'));

    await Promise.all(
      imgs.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve();
              return;
            }

            img.onload = () => resolve();
            img.onerror = () => resolve();
          })
      )
    );
  };
  const loadImage = (src: string) =>
    new Promise<HTMLImageElement>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.src = src;
    });
  const getLogoDimensions = (
    imgWidth: number,
    imgHeight: number,
    targetHeight: number
  ) => {
    const ratio = targetHeight / imgHeight;
    return {
      width: imgWidth * ratio,
      height: targetHeight,
    };
  };
  const processElements = async (
    arr: ContentElementType[] | SolutionElementType[]
  ) => {
    if (!Array.isArray(arr)) return arr;
    return Promise.all(
      arr.map(async (element) => {
        if (element?.model?.value instanceof ImageType) {
          const url = element.model.value.imageUrl;
          if (url && !url.startsWith('data:')) {
            let fullUrl = getMediaBasePath(url, 'processedMediaBucket');
            fullUrl = isGifMp4(fullUrl)
              ? fullUrl.replace('mp4', 'jpg')
              : fullUrl;
            const base64 = await fetchImageAsBase64(
              getMediaBasePath(fullUrl, 'processedMediaBucket')
            );
            if (base64) {
              element.model.value.imageUrl = base64;
            }
          }
        }
        return element;
      })
    );
  };
  const processOptions = async (arr: OptionsType[]) => {
    if (!Array.isArray(arr)) return arr;
    return Promise.all(
      arr.map(async (option) => {
        if (
          option?.optionType?.value instanceof ImageOption &&
          option.optionType.value.image instanceof ImageType
        ) {
          const url = option.optionType.value.image.imageUrl;
          if (url && !url.startsWith('data:')) {
            let fullUrl = getMediaBasePath(url, 'processedMediaBucket');
            fullUrl = isGifMp4(fullUrl)
              ? fullUrl.replace('mp4', 'jpg')
              : fullUrl;
            const base64 = await fetchImageAsBase64(
              getMediaBasePath(fullUrl, 'processedMediaBucket')
            );
            if (base64) {
              option.optionType.value.image.imageUrl = base64;
            }
          }
        }
        return option;
      })
    );
  };
  const getSubjectsClassFilenamePrefix = () => {
    const sections = assessment?.multiSubjectSections ?? [];
    const subjectNames = [
      ...new Set(
        sections
          .map((section) => {
            const name =
              getTeacherSubjectName(
                section.subjectId,
                user_info?.teachClassSubjects
              ) || getSubjectNameFromEnum(section.subject);
            return name?.trim();
          })
          .filter((name): name is string => !!name)
      ),
    ];
    const classPart = assessment?.assessment?.class
      ? String(assessment.assessment.class)
      : '';
    const parts = [...subjectNames];
    if (classPart) parts.push('Class_' + classPart);
    return parts.length ? `${parts.join('_')}_` : '';
  };
  React.useImperativeHandle(ref, () => ({
    handlePrint: async ({ title = '', setName, showAnswer }) => {
      try {
        if (setLoading) setLoading(true);

        // --- Step 1: Preprocess Questions before Rendering ---
        const processedQuestions: Question[] = [];
        if (questionsList) {
          for (let i = 0; i < questionsList.length; i++) {
            const question = new Question(questionsList[i]);

            if (question.question?.model?.value?.commonQuestionContent) {
              // Elements
              question.question.model.value.commonQuestionContent.elements =
                await processElements(
                  question.question.model.value.commonQuestionContent.elements
                );

              // Solutions
              question.question.model.value.commonQuestionContent.solution =
                await processElements(
                  question.question.model.value.commonQuestionContent.solution
                );

              // Options (for MCQ)
              if (
                question.question.model.value instanceof
                  McqSingleContentModel ||
                question.question.model.value instanceof McqMultipleContentModel
              ) {
                question.question.model.value.options = await processOptions(
                  question.question.model.value.options
                );
              }
            }

            processedQuestions.push(question);
          }
        }

        // --- Step 2: Render DOM with Processed Data ---
        // setFetchedQuestions(processedQuestions);
        setPrintState({
          // questionsList,
          showAnswer,
          setName,
        });
        setShowData(true);

        // Allow React render & paint to complete
        await new Promise((r) => setTimeout(r, 300));

        const el = printableRef.current;
        if (!el) throw new Error('Printable element not found');

        // --- Step 3: Wait for all images to load ---
        await waitForImagesToLoad(el);

        // --- Step 4: Wait for DOM layout stability ---
        await new Promise((r) => requestAnimationFrame(r));

        // --- Step 5: Generate PDF ---
        const assessmentTitle = sanitizeFilenamePart(
          title || assessment?.assessment?.assessmentTitle || 'Assessment'
        );
        const opt = {
          margin: [12, 12, 25, 12],
          filename: `${getSubjectsClassFilenamePrefix()}${assessmentTitle}${
            setName ? `_${sanitizeFilenamePart(setName)}` : ''
          }${showAnswer ? '_Answers' : '_Questions'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: {
            mode: ['css', 'legacy', 'whiteline'],
            avoid: ['.avoid-break-element', 'img'],
          },
        };

        const pdf = await renderElementToPdf(el, opt);
        const totalPages = pdf.internal.getNumberOfPages();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const footerY = pageHeight - 8;
        const lineY = footerY - 8;

        // --- Logos ---

        const rightLogo = Geneologo;
        const leftLogo = schoolLogo
          ? await fetchImageAsBase64(schoolLogo)
          : undefined;
        const logoY = 5;

        // --- Step 5: Add header logos + footer ---
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);

          // LEFT LOGO
          if (i === 1) {
            if (leftLogo) {
              const leftLogoHeight = 15; // Desired logo height in mm
              const leftImg = await loadImage(leftLogo);
              const leftLogoSize = getLogoDimensions(
                leftImg.width,
                leftImg.height,
                leftLogoHeight
              );
              pdf.addImage(
                leftLogo,
                'PNG',
                10,
                logoY,
                leftLogoSize.width,
                leftLogoSize.height
              );

              const rightLogoHeight = 10; // Desired logo height in mm
              // RIGHT LOGO
              const rightImg = await loadImage(rightLogo);
              const rightLogoSize = getLogoDimensions(
                rightImg.width,
                rightImg.height,
                rightLogoHeight
              );
              pdf.addImage(
                rightLogo,
                'PNG',
                pageWidth - rightLogoSize.width - 10,
                5,
                rightLogoSize.width,
                rightLogoSize.height
              );
            }
          }

          // Footer line
          pdf.setFontSize(10);
          pdf.setDrawColor(150);
          pdf.setLineWidth(0.2);
          pdf.line(10, lineY, pageWidth - 10, lineY);

          if (i === 1) {
            pdf.text('© Copyright reserved.', 10, footerY, { align: 'left' });

            pdf.text(
              `This Paper consists of ${totalPages} printed pages.`,
              pageWidth / 2,
              footerY,
              { align: 'center' }
            );
          } else {
            pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, footerY, {
              align: 'center',
            });
          }
        }

        await pdf.save(opt.filename);

        // --- Step 6: Cleanup ---
        setShowData(false);
        if (setLoading) setLoading(false);
      } catch (error) {
        console.error('Error generating PDF:', error);

        dispatch(
          setToastInfo({
            label: 'Error while generating pdf',
            variant: 'error',
            open: true,
          })
        );

        if (setLoading) setLoading(false);
      }
    },
  }));
  return (
    <Box sx={{ display: 'none' }}>
      {showData && (
        <Box
          ref={printableRef}
          sx={{
            fontSize: `${printableFontSize}px`,
          }}
        >
          <PrintableAssessmentHtml
            assessmentContent={assessment}
            // questions={printState.questions}
            showAnswer={printState.showAnswer}
            setName={printState.setName}
          />
        </Box>
      )}
    </Box>
  );
});

PrintableAssessment.displayName = 'PrintableAssessment';

export default PrintableAssessment;
